import Star from '@mui/icons-material/Star'
import StarBorder from '@mui/icons-material/StarBorder'
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
  useTheme,
} from '@mui/material'
import { Account } from '@/types/accounts'

type AccountSectionProps = {
  title: string
  accounts: Account[]
  currentAccount: Account
  favoriteIds: string[]
  onSelect: (account: Account) => void
  onToggleFavorite: (accountId: string) => void
  emptyMessage?: string
  showStars?: boolean
  showKind?: boolean
  highlightedAccountId?: string
  highlightedRef?: React.MutableRefObject<HTMLElement | null>
}

export default function AccountSection({
  title,
  accounts,
  currentAccount,
  favoriteIds,
  onSelect,
  onToggleFavorite,
  emptyMessage,
  showStars,
  showKind,
  highlightedAccountId,
  highlightedRef,
}: AccountSectionProps) {
  const theme = useTheme()
  if (accounts.length === 0 && !emptyMessage) return null

  return (
    <Box>
      <ListSubheader
        disableSticky
        sx={{
          fontWeight: 700,
          pt: 2,
          px: 2,
          pb: 0.5,
          color: 'primary.main',
          lineHeight: 1,
        }}
      >
        {title}
      </ListSubheader>

      {accounts.length === 0 && emptyMessage ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: 'italic', px: 2, py: 1 }}
        >
          {emptyMessage}
        </Typography>
      ) : (
        <List dense disablePadding>
          {accounts.map((account) => {
            const isSelected = account.id === currentAccount.id
            const isHighlighted = account.id === highlightedAccountId
            return (
              <ListItemButton
                key={account.id}
                ref={
                  isHighlighted && highlightedRef
                    ? (el) => {
                        highlightedRef.current = el
                      }
                    : undefined
                }
                selected={isSelected}
                onClick={() => onSelect(account)}
                sx={{
                  backgroundColor: isHighlighted
                    ? theme.palette.action.focus
                    : isSelected
                    ? 'action.selected'
                    : 'transparent',
                }}
              >
                <ListItemText
                  primary={account.name}
                  secondary={showKind ? account.kind : undefined}
                  slotProps={{
                    primary: {
                      sx: { fontWeight: isSelected ? 600 : 400 },
                    },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggleFavorite(account.id)
                  }}
                  sx={{
                    color: favoriteIds.includes(account.id)
                      ? 'warning.main'
                      : 'action.disabled',
                    opacity: showStars ? 1 : 0,
                    transition: 'opacity 0.15s',
                    '.MuiListItemButton-root:hover &': { opacity: 1 },
                  }}
                >
                  {favoriteIds.includes(account.id) ? (
                    <Star fontSize="small" />
                  ) : (
                    <StarBorder fontSize="small" />
                  )}
                </IconButton>
              </ListItemButton>
            )
          })}
        </List>
      )}
    </Box>
  )
}
